// @ts-check
// claude-client.js
// Calls the /api/claude Pages Function — API key stays server-side.
import { fbAuth } from './firebase.js';

/**
 * @param {object} req
 * @param {Array<{ role: string, content: any }>} req.messages
 * @param {string} [req.system]
 * @param {number} [req.max_tokens]
 * @param {object} [req.output_config] structured-outputs config (output_config.format)
 * @param {string} [req.model] omit to use the proxy default
 * @returns {Promise<string>} the first content block's text
 */
export async function askClaude({ messages, system, max_tokens = 1024, output_config, model }) {
  // The /api/claude proxy now requires a Firebase ID token (audit #5). Attach
  // the current user's token so authenticated calls continue to work.
  const headers = { 'Content-Type': 'application/json' };
  const u = fbAuth?.currentUser;
  if (u) headers['Authorization'] = `Bearer ${await u.getIdToken()}`;

  const payload = { messages, system, max_tokens };
  // Optional model override. Callers that omit it keep the proxy default
  // (Haiku — invoice extraction); the semantic matcher opts into a stronger
  // model for fuzzy same-item judgment.
  if (model) payload.model = model;
  // Pass through structured-outputs config when supplied so the caller can
  // constrain the response to a JSON schema (output_config.format).
  if (output_config) payload.output_config = output_config;

  const res = await fetch('/api/claude', {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) {
    // The proxy's own errors are a string (`{ error: 'Unauthorized' }`), but a
    // passed-through Anthropic error is an object (`{ error: { type, message } }`).
    // Pull a real message out of either shape so we never surface "[object Object]".
    const err = data && data.error;
    const msg = typeof err === 'string' ? err
      : (err && err.message) ? err.message
      : `Claude API error (HTTP ${res.status})`;
    throw new Error(msg);
  }

  // With structured outputs, content[0].text is schema-valid JSON only when the
  // turn ends normally. A safety refusal or a hit token cap returns text that is
  // NOT valid JSON — surface those clearly instead of a cryptic parse failure.
  if (data.stop_reason === 'refusal') {
    throw new Error('Claude declined this request (stop_reason: refusal).');
  }
  if (data.stop_reason === 'max_tokens') {
    throw new Error('Claude response was truncated at max_tokens — increase max_tokens and retry.');
  }

  return data.content[0].text;
}
